/** Core slice — the reference `renderVals()` body (script lines 3727–5158) minus the two dashboard IIFEs (→ dashboard.ts) and the
 *  spread-in method slices (exec/review/…). Shell, account popover, navigation, prepare, risk rules/profile, TradeHub core,
 *  accounts/notifications/settings, AI composer chrome, misc dashboard tables. Strings and maths verbatim. */
import type { SyntheticEvent } from 'react';
import type { ViewModel } from '@/screens/generated/view-model';
import { api } from '@/client/api';
import { refreshDemoData } from '@/client/DataProvider';
import { signOut } from '@/client/session';
import { instrumentScale, trailingStop } from './helpers';
import type { Ctx } from './ctx';
import { MODES, NAV, RAIL, SCREEN_MODE, TITLES, type NavItem } from './nav';
import { initialState, type ModeId, type ScreenId } from './state';
import { isRiskValueSet, saveRiskValue, toggleRiskRule } from './risk';

type InputEvent = SyntheticEvent<HTMLElement>;
const val = (e: InputEvent): string => (e.target as HTMLInputElement | HTMLTextAreaElement).value;

export function buildCore(c: Ctx) {
  const S = c.S,
    D = c.data.core;
  const M = MODES[S.mode];
  const scr = S.screen;

  const acctColor: Record<string, [string, string, string]> = {
    funded: ['var(--pos)', 'color-mix(in srgb, var(--pos) 14%, transparent)', 'FUNDED'],
    paused: ['var(--amb)', 'color-mix(in srgb, var(--amb) 14%, transparent)', 'PAUSED 24H'],
    live: ['var(--info)', 'color-mix(in srgb, var(--info) 14%, transparent)', 'PERSONAL'],
    challenge: ['var(--muted)', 'var(--l8)', ''],
  };
  const accounts = D.accounts.map((a) => {
    // Null equity/pnl is "no data" — a linked shell this build cannot read a balance for. Every derived string
    // says '—' rather than borrowing a number; zero would be a different (and false) claim.
    const nodata = a.equity === null || a.pnl === null;
    const equity = a.equity ?? 0,
      pnl = a.pnl ?? 0;
    const pos = pnl >= 0,
      ddPct = a.ddMax > 0 ? Math.round((a.dd / a.ddMax) * 100) : 0; // 0/0 is "no limit set", not NaN%
    const [bc, bg] = acctColor[a.status]!;
    const badge = acctColor[a.status]![2] || (a.phase || '').toUpperCase();
    return Object.assign({}, a, {
      nodata,
      equityStr: nodata ? '—' : '$' + equity.toLocaleString('en-US'),
      pnlStr: nodata
        ? 'No broker data — nothing has synced'
        : (pos ? '▲ +$' : '▼ −$') +
          Math.abs(pnl).toLocaleString('en-US') +
          ' (' +
          (pos ? '+' : '−') +
          Math.abs(a.prog || (pnl / (equity || 1)) * 100).toFixed(2) +
          '%)',
      pnlCol: nodata ? 'var(--muted2)' : pos ? 'var(--pos)' : 'var(--neg)',
      badge,
      badgeFg: bc,
      badgeBg: bg,
      hasTarget: !!a.target,
      targetStr: a.target
        ? '+$' + pnl.toLocaleString('en-US') + ' / $' + a.target.toLocaleString('en-US')
        : '',
      targetPct: a.target ? Math.min(100, (pnl / a.target) * 100).toFixed(0) + '%' : '0%',
      ddStr: nodata ? '—' : '$' + a.dd.toLocaleString('en-US') + ' / $' + a.ddMax.toLocaleString('en-US'),
      ddPct: ddPct + '%',
      ddCol: ddPct > 70 ? 'var(--neg-fill)' : ddPct > 50 ? 'var(--amb)' : 'var(--pos)',
      foot: a.days + 'd · ' + a.wins + ' W / ' + (a.days - a.wins) + ' L',
    });
  });
  const acctGroup = (a: { status: string }) =>
    a.status === 'live' ? 'personal' : a.status === 'challenge' ? 'challenge' : 'funded';
  const acctFilter = S.acctFilter || 'all';
  const acctFiltered = acctFilter === 'all' ? accounts : accounts.filter((a) => acctGroup(a) === acctFilter);
  const acctTabs = (
    [
      ['all', 'All', accounts.length],
      ['funded', 'Funded', accounts.filter((a) => acctGroup(a) === 'funded').length],
      ['challenge', 'Challenge', accounts.filter((a) => acctGroup(a) === 'challenge').length],
      ['personal', 'Personal', accounts.filter((a) => acctGroup(a) === 'personal').length],
    ] as const
  ).map(([id, label, n]) => ({
    label,
    n,
    go: () => c.set({ acctFilter: id }),
    bg: acctFilter === id ? 'var(--rail)' : 'transparent',
    fg: acctFilter === id ? 'var(--rail-ink)' : 'var(--muted)',
    fw: acctFilter === id ? '600' : '500',
  }));
  const acctLedger = D.acctLedger;

  const rulesMeta = D.rulesMeta;
  /** AUTHORITATIVE when loaded (mandate §1): each row's on-state is the server catalogue's, and a toggle is a
   *  real riskRules.update reconciled from the response. Disabling is a WEAKENING — it queues (ADR-014) and
   *  the row says so instead of flipping early. */
  const ruleServer = (k: string) => c.S.risk?.rules.find((r) => r.key === k) ?? null;
  const rules = rulesMeta.map((r) => {
    const srv = c.S.risk?.loaded ? ruleServer(r.k) : null;
    const on = srv ? srv.enabled : !!S.ruleOn[r.k];
    const queued = srv?.effectiveAt ?? null;
    const t = c.tog(on);
    // Card params show the SERVER's current values when loaded (the design meta is the pre-load skeleton),
    // so an edited parameter re-renders from the read-back, not from the literal it started at.
    const params = srv ? r.params.map((p) => ({ ...p, v: srv.params[p.k] ?? p.v })) : r.params;
    return Object.assign({}, r, {
      on,
      params,
      edit: () => {
        if (!c.S.risk?.loaded) {
          c.flash('Risk rules are still loading from the server — a moment.', 'mut');
          return;
        }
        c.set((s) =>
          s.risk
            ? {
                risk: {
                  ...s.risk,
                  ruleEditor: { key: r.k, name: r.name, params: params.map((p) => ({ ...p })) },
                },
              }
            : {},
        );
      },
      toggle: () => {
        if (!c.S.risk?.loaded) {
          c.flash('Risk rules are still loading from the server — a moment.', 'mut');
          return;
        }
        void toggleRiskRule(c, r.k, !on, r.name);
      },
      trackBg: t.trackBg,
      knob: t.knob,
      knobBg: t.knobBg,
      status: queued ? 'Disabling…' : on ? 'Active' : 'Disabled',
      statusFg: queued ? 'var(--amb)' : on ? 'var(--pos)' : 'var(--muted2)',
      statusBd: on ? 'color-mix(in srgb, var(--pos) 35%, transparent)' : 'var(--l12)',
    });
  });
  const activeRules = rules.filter((r) => r.on).length;

  const rcMeta = D.rcMeta;
  /** AUTHORITATIVE when loaded (mandate §1): on-state and value come from the SERVER's profile — the same
   *  version the funnel derives its thresholds from — and a toggle is a real riskProfile.update reconciled
   *  from the response. The local skeleton (S.rc + the meta's own value) renders only until the load lands,
   *  and the design kept the two consistent so the swap moves no pixels. A weakening in the refractory queue
   *  wears its 'queued' note on the card instead of showing the looser value early. */
  const RK = S.risk;
  const mkRc = (k: string) => {
    const Mx = rcMeta[k]!;
    const serverValue = RK?.loaded ? RK.values[k] : undefined;
    const on = RK?.loaded ? isRiskValueSet(serverValue) : !!S.rc[k];
    const queued = RK?.loaded ? (RK.pending.find((p) => p.key === k) ?? null) : null;
    const t = c.tog(on),
      open = S.rcOpen === k;
    const busy = RK?.busy === k;
    return Object.assign({ k, on, open, closed: !open }, Mx, t, {
      value: on ? (serverValue ?? Mx.value) : 'Off',
      valueFg: on ? 'var(--text2)' : 'var(--faint)',
      nameFg: on ? 'var(--text)' : 'var(--muted)',
      cardBd: on ? 'color-mix(in srgb, var(--pos) 26%, var(--card-line))' : 'var(--card-line)',
      tileBg: on ? 'color-mix(in srgb, var(--pos) 10%, transparent)' : 'var(--l5)',
      tileBd: on ? 'color-mix(in srgb, var(--pos) 28%, transparent)' : 'var(--l8)',
      tileFg: on ? 'var(--pos)' : 'var(--ghost)',
      caret: open ? 'rotate(180deg)' : 'rotate(0deg)',
      expandText: open ? 'Hide details' : 'Details',
      expandLabel: (open ? 'Collapse ' : 'Expand ') + Mx.name,
      toggleLabel: (on ? 'Disable ' : 'Enable ') + Mx.name + (queued ? ' (change queued)' : ''),
      queuedNote: queued
        ? `Queued: ${queued.to} applies ${queued.effectiveAt.slice(11, 16)} UTC — the current limit holds until then.`
        : busy
          ? 'Saving…'
          : '',
      expand: () => c.set((s) => ({ rcOpen: s.rcOpen === k ? null : k })),
      edit:
        RK?.loaded && on
          ? () =>
              c.set((s) =>
                s.risk
                  ? { risk: { ...s.risk, editor: { key: k, name: Mx.name, value: serverValue ?? '' } } }
                  : {},
              )
          : undefined,
      toggle: () => {
        if (!RK?.loaded) {
          c.flash('Risk settings are still loading from the server — a moment.', 'mut');
          return;
        }
        const restore = /^not set$/i.test((Mx.value ?? '').trim()) ? (Mx.params?.[0]?.v ?? 'On') : Mx.value;
        void saveRiskValue(c, k, on ? 'Not set' : restore, Mx.name);
      },
    });
  };
  const rcSections = [
    {
      icon: 'ph-trend-down',
      label: 'Loss Limits',
      blurb: 'Stop the day before it compounds',
      scopeNote: 'session, week, account',
      items: ['daily', 'weekly', 'ddcb', 'ptl'].map(mkRc),
    },
    {
      icon: 'ph-chart-bar',
      label: 'Trade Limits',
      blurb: 'Bound each entry and the book',
      scopeNote: 'checked at order time',
      items: ['dtl', 'mps', 'mcp', 'slreq'].map(mkRc),
    },
    {
      icon: 'ph-lock-simple',
      label: 'Lockout Controls',
      blurb: 'Guard against tilt and bad windows',
      scopeNote: 'behavioural guards',
      items: ['cll', 'cooldown', 'news', 'window', 'revenge'].map(mkRc),
    },
  ].map((sec) => {
    const n = sec.items.filter((i) => i.on).length;
    return Object.assign(sec, {
      activeN: n,
      totalN: sec.items.length,
      count: n + ' of ' + sec.items.length + ' active',
      countFg: n === 0 ? 'var(--neg)' : n === sec.items.length ? 'var(--pos)' : 'var(--text)',
    });
  });
  const rcActive = rcSections.reduce((a, s) => a + s.items.filter((i) => i.on).length, 0);
  const rcTotal = rcSections.reduce((a, s) => a + s.items.length, 0);

  const rpTabs = ['controls', 'maturity', 'audit'].map((id) => ({
    id,
    label: id === 'controls' ? 'Risk Controls' : id === 'maturity' ? 'Maturity' : 'Audit Log',
    icon:
      id === 'controls'
        ? 'ph-shield-check'
        : id === 'maturity'
          ? 'ph-lightning'
          : 'ph-clock-counter-clockwise',
    go: () => c.set({ rpTab: id }),
    bg: S.rpTab === id ? 'var(--rail)' : 'transparent',
    fg: S.rpTab === id ? 'var(--rail-ink)' : 'var(--muted)',
    bd: 'transparent',
  }));

  const moods = (
    [
      ['excellent', 'ph-thumbs-up', 'Excellent'],
      ['good', 'ph-smiley', 'Good'],
      ['neutral', 'ph-smiley-meh', 'Neutral'],
      ['poor', 'ph-smiley-sad', 'Poor'],
      ['terrible', 'ph-thumbs-down', 'Terrible'],
    ] as const
  ).map(([id, icon, label]) => ({
    id,
    icon,
    label,
    go: () => c.set({ mood: id }),
    bd: S.mood === id ? 'color-mix(in srgb, var(--acc-fill) 50%, transparent)' : 'var(--l10)',
    bg: S.mood === id ? 'var(--l6)' : 'var(--surface)',
    fg: S.mood === id ? 'var(--text)' : 'var(--muted)',
  }));

  const focusAll = [
    'Patience',
    'Discipline',
    'Risk Management',
    'Entry Timing',
    'Exit Strategy',
    'Position Sizing',
    'Trend Following',
    'Scalping',
    'News Trading',
    'Technical Analysis',
    'Price Action',
    'Journaling',
  ];
  const focusChips = focusAll.map((f) => {
    const on = S.focus.includes(f);
    return {
      label: f,
      on,
      go: () => c.set((s) => ({ focus: on ? s.focus.filter((x) => x !== f) : s.focus.concat(f) })),
      bd: on ? 'color-mix(in srgb, var(--acc-fill) 50%, transparent)' : 'var(--l10)',
      bg: on ? 'var(--l6)' : 'transparent',
      fg: on ? 'var(--text)' : 'var(--muted)',
    };
  });

  const stratAll = [
    'London Open Reversal',
    'Trend Continuation',
    'Breakout Pullback',
    'Asia Range Fade',
    'News Fade',
    'Liquidity Sweep',
    'Mean Reversion',
    'Session Close Momentum',
  ];
  const stratChips = stratAll.map((f) => {
    const on = S.armed.includes(f);
    return {
      label: f,
      go: () => c.set((s) => ({ armed: on ? s.armed.filter((x) => x !== f) : s.armed.concat(f) })),
      bd: on ? 'color-mix(in srgb, var(--acc-fill) 50%, transparent)' : 'var(--l10)',
      bg: on ? 'var(--l6)' : 'transparent',
      fg: on ? 'var(--text)' : 'var(--muted)',
    };
  });

  /** Arming the day (FLOW-SESSION), behind the reference's own "Start Session" button.
   *
   *  Everything the trader has set on this screen — mood, energy, focus, the armed playbook, the rule limits — is sent
   *  once, and the session that comes back is the one the order funnel will read. The screen does not keep its own
   *  idea of "armed" afterwards: two answers to "am I armed?" is how a trader ends up submitting into a session that
   *  closed an hour ago.
   *
   *  `budgetConfirmed` is sent true because pressing this button *is* the confirmation — the caps are on screen above
   *  it. The flag exists for the surfaces where they are not, and voice is the one that matters (§11.2): a spoken
   *  "arm my day" has no caps in view, so the orchestrator has to read them back before this can be true. */
  const RC_META: [string, string, string][] = [
    ['daily', 'Daily Loss Limit', '2.0% of balance'],
    ['weekly', 'Weekly Loss Limit', 'Not set'],
    ['ddcb', 'Drawdown Circuit Breaker', '6.0% from peak'],
    ['ptl', 'Profit Target Lock', 'Not set'],
    ['dtl', 'Daily Trade Limit', '5 trades'],
    ['cll', 'Consecutive Loss Lock', 'Not set'],
  ];

  /** The numeric part of a limit's value ("5 trades" → 5). AUTHORITATIVE when the risk state is loaded: the
   *  cap the arm ships is the SERVER's own value — the same one the funnel enforces — so the number the trader
   *  confirmed and the number the gate applies cannot be two documents. A disabled/unset limit falls back to
   *  the default rather than to zero: off means "no limit set", not "a limit of nothing". */
  const capValue = (key: string, fallback: number): number => {
    if (c.S.risk?.loaded) {
      const value = c.S.risk.values[key];
      if (!isRiskValueSet(value)) return fallback;
      const parsed = Number.parseFloat((value ?? '').replace(/[^0-9.]/g, ''));
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }
    if (!S.rc[key]) return fallback;
    const row = RC_META.find(([k]) => k === key);
    const parsed = Number.parseFloat((row?.[2] ?? '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const armSession = async () => {
    if (S.armPending) return;
    c.set({ armPending: true });
    try {
      const session = await api.prepare.session.arm({
        idempotencyKey: `arm-${Date.now()}`,
        mood: S.mood || 'steady',
        energy: S.energy,
        // The caps the screen is showing, read from the same rows it renders (RC_META) rather than from a second
        // list — a cap the trader reads and a cap the funnel enforces must be the same number or the limit is
        // decorative. Only enabled limits are sent; a toggle that is off is not a cap of zero.
        caps: {
          maxTrades: capValue('dtl', 5),
          maxLossR: capValue('daily', 3),
          maxRiskPerTradePct: capValue('ddcb', 1),
        },
        checklist: [],
        strategies: S.armed.length ? S.armed : stratAll.slice(0, 1),
        // The brief's stated goal rides the arm (mandate §4) — it lands on the session row and titles the
        // day's journal entry instead of dying in this tab.
        dailyGoal: S.briefGoal.trim() || undefined,
        budgetConfirmed: true,
      });
      c.set({ armPending: false, sessionState: session.state, sessionId: session.sessionId });
      c.flash('Session armed. Your limits are in force.', 'pos');
    } catch (error) {
      c.set({ armPending: false });
      c.flash(error instanceof Error ? error.message : 'The session could not be armed.', 'neg');
    }
  };

  const prepRules = rulesMeta.map((r) => {
    const srv = c.S.risk?.loaded ? ruleServer(r.k) : null;
    const on = srv ? srv.enabled : !!S.ruleOn[r.k];
    return {
      name: r.name,
      type: r.type,
      on,
      icon: on ? 'ph-check-circle' : 'ph-circle',
      iconFg: on ? 'var(--pos)' : 'var(--ghost)',
      nameFg: on ? 'var(--text)' : 'var(--muted2)',
      status: srv?.effectiveAt ? 'Disabling…' : on ? 'Active' : 'Off',
      statusFg: on ? 'var(--pos)' : 'var(--faint)',
      toggle: () => {
        if (!c.S.risk?.loaded) {
          c.flash('Risk rules are still loading from the server — a moment.', 'mut');
          return;
        }
        void toggleRiskRule(c, r.k, !on, r.name);
      },
    };
  });
  const prepLimits = RC_META.map(([k, name, v]) => {
    const serverVal = c.S.risk?.loaded ? c.S.risk.values[k] : undefined;
    const on = c.S.risk?.loaded ? isRiskValueSet(serverVal) : !!S.rc[k];
    return {
      name,
      val: on ? (serverVal ?? v) : '—',
      on,
      icon: on ? 'ph-shield-check' : 'ph-shield-slash',
      iconFg: on ? 'var(--pos)' : 'var(--ghost)',
      nameFg: on ? 'var(--text)' : 'var(--muted2)',
      valFg: on ? 'var(--text2)' : 'var(--faint)',
    };
  });
  const ruleActive = prepRules.filter((r) => r.on).length;
  const limitActive = prepLimits.filter((r) => r.on).length;
  const prepPrompts = [
    'What would make me close early today?',
    'Where did I break a rule last session?',
    'What does a good session look like at 17:00?',
    'Which setup am I most likely to force?',
  ];
  const preNoteChips = prepPrompts.map((p) => ({
    label: p,
    go: () => c.set((s) => ({ preNote: (s.preNote ? s.preNote.replace(/\s*$/, '\n') : '') + p + '\n' })),
  }));

  const trades = D.dashTrades.map(([date, side, entry, exit, pnl, d]) => ({
    date,
    side,
    entry,
    exit,
    pnl,
    dur: d,
    sideBg:
      side === 'BUY'
        ? 'color-mix(in srgb, var(--pos) 12%, transparent)'
        : 'color-mix(in srgb, var(--neg-fill) 14%, transparent)',
    sideFg: side === 'BUY' ? 'var(--pos)' : 'var(--neg)',
  }));

  const tradeRanges = ['Today', 'This Week', 'This Month', 'This Year', 'All Time'].map((r) => {
    const on = S.tradeRange === r.toLowerCase() || (S.tradeRange === 'all' && r === 'All Time');
    return {
      label: r,
      go: () => c.set({ tradeRange: r.toLowerCase() }),
      on,
      bg: on ? 'color-mix(in srgb, var(--amb) 12%, transparent)' : 'transparent',
      fg: on ? 'var(--amb)' : 'var(--muted)',
      bd: on ? 'var(--l8)' : 'var(--l10)',
    };
  });

  const wlChips = S.wl.map((sym) => ({
    sym,
    // §4: removal is the server's act too — the list re-renders from the response's items.
    remove: () =>
      void api.prepare.watchlist
        .remove({ symbol: sym })
        .then((out) => c.set({ wl: out.items.map((i) => i.symbol) }))
        .catch((error: unknown) =>
          c.flash(error instanceof Error ? error.message : sym + ' could not be removed.', 'neg'),
        ),
  }));

  const ticketTabs = (
    [
      ['market', 'Market'],
      ['pending', 'Pending'],
      ['askai', 'Ask AI'],
    ] as const
  ).map(([id, label]) => ({
    id,
    label,
    go: () => c.set({ ticketTab: id }),
    fg: S.ticketTab === id ? 'var(--text)' : 'var(--muted)',
    bb: S.ticketTab === id ? 'var(--acc-fill)' : 'transparent',
  }));

  // ── Open positions: live float P&L and "SL → break-even" ──
  const chLive = c.chart();
  const num = (v: unknown) => parseFloat(String(v).replace(/,/g, ''));
  const openLive = S.openPos.map((p, i) => {
    const openPx = num(p.open),
      curPx = num(chLive.lastPrice),
      vol = num(p.vol) || 0;
    const dir = String(p.type).indexOf('BUY') === 0 ? 1 : -1;
    const flt = (curPx - openPx) * dir * vol * 100;
    const slPx = num(p.sl);
    // "At break-even" means the stop is *at* entry, and how close counts as "at" depends on the instrument: half a
    // cent is touching on gold and two full pips on EURUSD. Scaled off the quoted price rather than fixed at 0.005,
    // which flagged every EURUSD stop within 50 pips as already at break-even.
    const beTolerance = Math.max(openPx * 1.25e-6, Number.EPSILON);
    const atBe = isFinite(slPx) && Math.abs(slPx - openPx) < beTolerance;
    const inProfit = flt > 0;
    // The same trailing level the chart draws — one helper, so the marker and this row cannot disagree about where
    // the stop is. A trader reading one against the other is exactly who would be hurt if they did.
    const trailedSl = S.ruleOn['trail']
      ? trailingStop({ entry: openPx, stop: slPx, current: curPx, isLong: dir === 1 })
      : slPx;
    return {
      i,
      p,
      openPx,
      curPx,
      flt,
      atBe,
      canBe: inProfit && !atBe,
      inProfit,
      trailedSl,
      decimals: instrumentScale(p.sym).decimals,
    };
  });
  const beEligible = openLive.filter((o) => o.canBe);
  const beAtAlready = openLive.filter((o) => o.inProfit && o.atBe);
  const toBe = (idxs: number[], note: string) => {
    if (!idxs.length) return;
    c.set((s) => ({
      openPos: s.openPos.map((p, i) =>
        idxs.indexOf(i) < 0
          ? p
          : Object.assign({}, p, { sl: num(p.open).toFixed(instrumentScale(p.sym).decimals) }),
      ),
    }));
    c.flash(note, 'pos');
  };
  interface BeAction {
    label: string;
    disabled: boolean;
    op: string;
    fg: string;
    bd: string;
    title: string;
    go: () => void;
  }
  type PosRow =
    | [string, string, string, string, string, string, string, string, number]
    | [string, string, string, string, string, string, string, string, number, BeAction, boolean];
  interface PosSet {
    label: string;
    total: number;
    empty: string;
    rows: PosRow[];
  }
  const POS_DATA: Record<string, PosSet> = {
    positions: {
      label: 'Open Positions',
      total: S.openPos.length,
      empty: 'No open positions — place one from the order desk',
      rows: openLive.map((o): PosRow => {
        const p = o.p;
        const pnlStr = (o.flt >= 0 ? '+$' : '−$') + Math.abs(o.flt).toFixed(2);
        return [
          p.sym,
          p.type,
          p.vol,
          p.open,
          chLive.lastPrice,
          // Break-even wins over the trail: moving the stop to entry is a deliberate act, and it should not be
          // silently walked further by a rule. Shown to the instrument's own decimals — "1.14" is not a EURUSD price.
          o.atBe
            ? o.openPx.toFixed(o.decimals)
            : isFinite(o.trailedSl)
              ? o.trailedSl.toFixed(o.decimals)
              : p.sl,
          p.tp,
          pnlStr,
          o.flt > 0 ? 1 : o.flt < 0 ? -1 : 0,
          {
            label: o.atBe ? 'At BE' : 'SL → BE',
            disabled: !o.canBe,
            op: o.canBe ? '1' : '0.45',
            fg: o.atBe ? 'var(--pos)' : o.canBe ? 'var(--pos)' : 'var(--faint)',
            bd: o.canBe ? 'color-mix(in srgb, var(--pos) 45%, transparent)' : 'var(--l8)',
            title: o.atBe
              ? 'Stop already sits at the ' + o.openPx.toFixed(o.decimals) + ' entry'
              : o.inProfit
                ? 'Move stop to the ' +
                  o.openPx.toFixed(o.decimals) +
                  ' entry — locks out the loss, leaves ' +
                  pnlStr +
                  ' running'
                : 'Only available while the position is in profit — currently ' + pnlStr,
            go: () =>
              o.canBe
                ? toBe([o.i], p.sym + ' stop moved to break-even at ' + o.openPx.toFixed(o.decimals))
                : c.flash(
                    o.atBe
                      ? p.sym + ' is already at break-even'
                      : p.sym + ' is not in profit — break-even would sit behind price',
                    'mut',
                  ),
          },
          o.atBe,
        ];
      }),
    },
    pending: {
      label: 'Pending Orders',
      total: 2,
      empty: 'No pending orders',
      rows: D.posPending.map((r) => [...r] as PosRow),
    },
    closed: {
      label: 'Closed Positions',
      total: 17,
      empty: 'No closed positions in range',
      rows: D.posClosed.map((r) => [...r] as PosRow),
    },
    orders: {
      label: 'Order History',
      total: 24,
      empty: 'No orders in range',
      rows: D.posOrders.map((r) => [...r] as PosRow),
    },
    alerts: { label: 'Alerts', total: 3, empty: 'No alerts set', rows: [] },
    logs: { label: 'Logs', total: 0, empty: 'No engine activity this session', rows: [] },
  };
  const posTabs = (
    [
      ['positions', 'Positions'],
      ['pending', 'Pending'],
      ['closed', 'Closed Positions'],
      ['orders', 'Order History'],
      ['alerts', 'Alerts'],
      ['logs', 'Logs'],
    ] as const
  ).map(([id, label]) => ({
    id,
    label,
    go: () => c.set({ posTab: id, posPage: 0 }),
    count: POS_DATA[id]!.total,
    fg: S.posTab === id ? 'var(--text)' : 'var(--muted)',
    bb: S.posTab === id ? 'var(--acc-fill)' : 'transparent',
  }));
  const PD = POS_DATA[S.posTab] || POS_DATA['positions']!;

  /* ── the table's view pipeline (mandates §7/§9): account scope → symbol filter → page ─────────────────────
   *  Row/live pairing happens FIRST, so a filtered positions tab still addresses the right server position —
   *  filtering the display can never retarget a close/modify at a neighbouring row. */
  const posPairs = PD.rows.map((r, i) => ({
    r,
    live: S.posTab === 'positions' ? S.openPos[i] : undefined,
  }));
  const posAcctScoped =
    S.thAcct && S.posTab === 'positions'
      ? posPairs.filter((p) => p.live && p.live.accountId === S.thAcct)
      : posPairs;
  const posQuery = S.posFilterSym.trim().toUpperCase();
  const posFiltered = posQuery
    ? posAcctScoped.filter((p) => String(p.r[0]).toUpperCase().includes(posQuery))
    : posAcctScoped;
  const POS_PAGE_SIZE = 10;
  const posPageMax = Math.max(0, Math.ceil(posFiltered.length / POS_PAGE_SIZE) - 1);
  const posPage = Math.min(S.posPage, posPageMax); // a filter that shrank the set never strands the view on a blank page
  const posPaged = posFiltered.slice(posPage * POS_PAGE_SIZE, posPage * POS_PAGE_SIZE + POS_PAGE_SIZE);

  const posRows = posPaged.map(({ r, live }) => {
    // Row actions exist only on the OPEN positions tab, addressed by the server's position id (mandate §2). The
    // ask-side is a pure state set; the confirmed operation lives in the exec slice and reads server truth back.
    const busy = live
      ? S.posBusy === `close-${live.id}` || S.posBusy === `modify-${live.id}` || S.posBusy === 'closeAll'
      : false;
    return {
      sym: r[0],
      type: r[1],
      vol: r[2],
      open: r[3],
      cur: r[4],
      sl: r[5],
      tp: r[6],
      pnl: r[7],
      pnlFg: r[8] > 0 ? 'var(--pos)' : r[8] < 0 ? 'var(--neg)' : 'var(--text2)',
      be: r[9] || null,
      hasBe: !!r[9],
      atBe: !!r[10],
      slFg: r[10] ? 'var(--pos)' : 'var(--muted2)',
      actBusy: busy ? '1' : '',
      modify: live
        ? () => {
            if (S.posBusy) return;
            c.set({
              posModify: {
                id: live.id,
                sym: live.sym,
                sl: live.sl === '—' ? '' : live.sl,
                tp: live.tp === '—' ? '' : live.tp,
                decimals: instrumentScale(live.sym).decimals,
              },
            });
          }
        : undefined,
      close: live
        ? () => {
            if (S.posBusy) return;
            c.set({ posCloseConfirm: { id: live.id, sym: live.sym, vol: live.vol, pnl: live.pnl } });
          }
        : undefined,
    };
  });
  const posShown = posPaged.length;

  const csvEscape = (v: string) => (/[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);
  const POS_CSV_HEADERS: Record<string, readonly string[]> = {
    positions: ['Symbol', 'Type', 'Volume', 'Open', 'Current', 'SL', 'TP', 'PnL'],
    pending: ['Symbol', 'Type', 'Volume', 'Price', 'Current', 'SL', 'TP', 'Status'],
    closed: ['Symbol', 'Type', 'Volume', 'Open', 'Close', 'SL', 'TP', 'PnL'],
    orders: ['Symbol', 'Type', 'Volume', 'Price', 'Status', 'SL', 'TP', 'Result'],
    alerts: ['Alert'],
    logs: ['Entry'],
  };
  const posExportCsv = () => {
    // The export IS the visible scope: the active tab after account scope and symbol filter, every page of it —
    // what you filtered to is what you get, never a silently wider (or narrower) set.
    const rows = posFiltered.map((p) => p.r.slice(0, 8).map((v) => csvEscape(String(v ?? ''))));
    if (rows.length === 0) {
      c.flash('Nothing to export on this tab.', 'mut');
      return;
    }
    const header = (POS_CSV_HEADERS[S.posTab] ?? POS_CSV_HEADERS['positions'])!;
    const csv = [header.slice(0, rows[0]!.length).join(','), ...rows.map((cells) => cells.join(','))].join(
      '\r\n',
    );
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'pointfive-' + S.posTab + '-' + new Date().toISOString().slice(0, 10) + '.csv';
    a.click();
    URL.revokeObjectURL(a.href);
    c.flash(
      'Exported ' + rows.length + (rows.length === 1 ? ' row' : ' rows') + ' (' + PD.label + ').',
      'pos',
    );
  };

  /* ── the account dimension (mandate §7, ADR-016) ─────────────────────────────────────────────────────────
   *  `thAcct: null` is not "no account" — it is the session's own account, the one the server armed, which the
   *  client does not presume to name. An explicit pick follows the trader atomically: the ticket (accountId on
   *  the wire), the confirm draft, the positions view, and the balance strip all read this one key. */
  const thSelected = S.thAcct ? (accounts.find((a) => a.id === S.thAcct) ?? null) : null;
  const thFloat = openLive.reduce(
    (sum, o) => (S.thAcct && S.openPos[o.i]?.accountId !== S.thAcct ? sum : sum + o.flt),
    0,
  );
  const thAcctChoices = [
    {
      id: '',
      label: 'Session default',
      detail: 'The account this session is armed on — the server decides.',
      on: !S.thAcct,
      simulated: false,
      go: () => c.set({ thAcct: null, thAcctOpen: false, posPage: 0 }),
    },
    ...accounts.map((a) => ({
      id: a.id,
      label: a.firm,
      detail: a.plan + ' · ' + a.equityStr,
      on: S.thAcct === a.id,
      simulated: /simulated/i.test(a.plan),
      go: () => c.set({ thAcct: a.id, thAcctOpen: false, posPage: 0 }),
    })),
  ];

  // stratOb/stratLdn moved to model/strategies.ts (server-backed; mandate §5) — slices spread after core, so
  // that slice's fields win.

  const news = D.news;
  const calendar = D.calendar;

  // `||`, not `??`: App passes avatarSrc:'' as the neutral prop, and an empty string must not mask the
  // photo the trader actually uploaded (S.avatarSrc, hydrated from me.profile and set by setAvatar).
  const avatar = c.props.avatarSrc || S.avatarSrc;
  const nav = (() => {
    const counts: Record<string, string> = {
      signals: '3',
      trades: '17',
      notifications: '3',
      accounts: '6',
      riskrules: String(activeRules),
    };
    const row = (it: NavItem) => {
      const on = scr === it.id;
      if (it.id === 'dashboard')
        return Object.assign({}, it, {
          go: () => c.go(it.id),
          count: '',
          bg: 'var(--rail)',
          bd: 'transparent',
          fg: 'var(--rail-ink)',
          ic: 'var(--rail-ink)',
          fw: '600',
          railBg: on ? 'var(--rail-on)' : 'transparent',
          railFg: on ? 'var(--rail-ink)' : 'var(--rail-dim)',
          railBottomBg: 'var(--rail)',
          railBottomFg: 'var(--rail-ink)',
        });
      return Object.assign({}, it, {
        go: () => c.go(it.id),
        count: counts[it.id] || '',
        bg: on ? 'var(--row-on)' : 'transparent',
        bd: on ? 'var(--row-on-line)' : 'transparent',
        fg: on ? 'var(--text)' : 'var(--text-mid)',
        ic: on ? 'var(--text)' : 'var(--muted)',
        // The inactive icon is dimmed rather than recoloured, so the row's weight reads before its hue does.
        icOp: on ? '1' : '0.62',
        fw: on ? '600' : '500',
        railBg: on ? 'var(--rail-on)' : 'transparent',
        railFg: on ? 'var(--rail-ink)' : 'var(--rail-dim)',
        railBottomBg: on ? 'var(--rail-on)' : 'var(--rail)',
        railBottomFg: 'var(--rail-ink)',
      });
    };
    const seen: Record<string, 1> = {};
    const main = ([{ id: 'dashboard', icon: 'ph-house', label: 'Dashboard' }] as NavItem[])
      .concat(RAIL[S.mode])
      .concat(NAV[S.mode].filter((n) => !RAIL[S.mode].some((r) => r.id === n.id)))
      .filter((i) => i.id !== 'settings' && !seen[i.id] && (seen[i.id] = 1));
    return {
      // Dashboard sits on its own above a rule; everything after it is grouped under the current mode's name, and
      // Risk Profile closes the second group. The separators are properties of the row rather than markup, because
      // the rail renders the same list collapsed and there is nowhere to put a divider there.
      navMain: main.map(row).map((r, i) => ({
        ...r,
        sepBefore: i === 1,
        groupLabel: i === 1 ? M.label : '',
        sepAfter: r.id === 'riskprofile' && i < main.length - 1,
      })),
      navSettings: row({ id: 'settings', icon: 'ph-gear-six', label: 'Settings' }),
    };
  })();

  const setSectionLabels: Record<string, string> = {
    profile: 'Profile',
    security: 'Security',
    lockout: 'Lockout Settings',
    notifications: 'Notifications',
    display: 'Display',
    trading: 'Trading Defaults',
    integrations: 'Integrations',
    data: 'Data Management',
    danger: 'Danger Zone',
  };

  return {
    showFxTicker: c.props.showFxTicker ?? true,
    modeBtns: (
      [
        ['prepare', 'Prepare', 'ph-sun', 'briefing'],
        ['execute', 'Execute', 'ph-arrows-left-right', 'tradehub'],
        ['review', 'Review', 'ph-book-open', 'journal'],
        ['analyze', 'Analyse', 'ph-chart-bar', 'analytics'],
        ['manage', 'Manage', 'ph-wrench', 'accounts'],
      ] as [ModeId, string, string, ScreenId][]
    ).map((r) => {
      const on = S.mode === r[0];
      return {
        label: r[1],
        icon: r[2],
        on,
        pad: on ? '4px 15px 4px 4px' : '4px',
        go: () => c.set({ mode: r[0], screen: r[3] }),
        bg: on ? 'var(--rail)' : 'transparent',
        hover: on ? 'var(--rail)' : 'var(--l6)',
        fg: on ? 'var(--rail-ink)' : 'var(--text-mid)',
        ring: on ? 'var(--rail-line)' : 'var(--l10)',
        hdrBg: on ? 'var(--rail-on)' : 'transparent',
        hdrHover: on ? 'var(--rail-on)' : 'var(--rail-hi)',
        hdrFg: on ? 'var(--rail-ink)' : 'var(--rail-dim)',
        hdrRing: on ? 'var(--rail-ink)' : 'var(--rail-line)',
        fw: on ? '600' : '500',
      };
    }),
    navOpen: S.navOpen,
    modeLabelUpper: M.label.toUpperCase(),
    modeIcon: M.icon,
    foldIcon: S.navOpen ? 'ph-caret-left' : 'ph-caret-right',
    foldTitle: S.navOpen ? 'Collapse navigation' : 'Expand navigation',
    toggleFold: () => c.set((s) => ({ navOpen: !s.navOpen })),
    navClosed: !S.navOpen,
    acctOpen: S.acctOpen,
    acctName: 'Omichael Nhamburo',
    acctEmail: 'omichael@pointfiveinc.com',
    acctPlan: 'Pro · Annual',
    avatarBg: avatar ? 'url("' + avatar + '")' : 'none',
    avatarInitialsHdr: avatar ? '' : 'ON',
    // The Settings profile circle — same photo, its own single-letter fallback.
    avatarInitialsSet: avatar ? '' : 'O',
    avatarInkHdr: 'var(--rail-ink)',
    toggleAcct: () =>
      c.set((s) => {
        const next = !s.acctOpen;
        if (next && typeof document !== 'undefined') {
          const close = (e: Event) => {
            if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Escape') return;
            const target = e.target as Element | null;
            if (e.type === 'pointerdown' && target && target.closest && target.closest('[data-acct]')) return;
            document.removeEventListener('pointerdown', close, true);
            document.removeEventListener('keydown', close, true);
            c.set({ acctOpen: false });
          };
          c.timers.after(0, () => {
            document.addEventListener('pointerdown', close, true);
            document.addEventListener('keydown', close, true);
          });
        }
        return { acctOpen: next };
      }),
    goNotifications: () => c.go('notifications'),
    goBilling: () => c.go('billing'),
    goRiskRules: () => c.go('riskrules'),
    goTradeHub: () => c.go('tradehub'),
    goTrades: () => c.go('trades'),
    goJournal: () => c.go('journal'),
    // Sign out (mandate §3). The pending flag survives until the navigation tears the page down — a second click
    // meets a disabled row, and a logout that FAILS to navigate leaves an honest, retryable state. Under the sim
    // binding there is no server session to revoke; the signed-out page is still the truthful destination.
    signOutPending: S.signOutPending,
    signOutGo: (event?: { preventDefault?: () => void }) => {
      event?.preventDefault?.();
      if (S.signOutPending) return;
      c.set({ signOutPending: true });
      signOut();
    },
    ...nav,
    themeAttr: S.theme,
    themeIcon: S.theme === 'dark' ? 'ph-sun-dim' : 'ph-moon-stars',
    toggleTheme: () => c.set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),
    composerOpen: S.composerOpen,
    toggleComposer: () => c.set((s) => ({ composerOpen: !s.composerOpen })),
    composerChips: [
      'Brief me on overnight',
      'Did I follow my rules today?',
      "What's killing my edge?",
      'Validate: XAUUSD long, stop −18',
    ],
    // navHome/navMode (reference `navItems`) are not bound by the template — dead keys, omitted

    // modeLabel: the package now binds only `modeLabelUpper` — dead key, omitted like its neighbours
    // setPrepare/setExecute/setAnalyze/setReview/setManage: returned by the reference, never bound (modeBtns carry `go`) — omitted
    // prep/exec/an/rv/mgBtn{Bg,Bd,Fg}: returned by the reference (modeBtn helper), never bound by the template — omitted
    isAnalytics: scr === 'analytics',
    isAiInsights: scr === 'aiinsights',
    isPfScore: scr === 'pfscore',
    isJournal: scr === 'journal',
    isReplay: scr === 'replay',
    isEod: scr === 'eod',
    isCompare: scr === 'compareaccts',
    isDigest: scr === 'perfdigest',
    isTradingIdentity: scr === 'tradingidentity',
    isStub: ([] as string[]).includes(scr), // every route now has a screen — the stub list emptied with Investors (§14)
    isInvestors: scr === 'investors', // composed screen (DEC-065); the package's stub copy invites the build
    isTradingIntel: scr === 'tradingintel', // composed gap screen (OD-1); the reference has no tradingintel markup
    isBilling: scr === 'billing', // MOD-BILLING; the package draws the entry point (account menu) but not the screen
    stubTitle: TITLES[scr] || '',
    stubIcon: (RAIL[S.mode].filter((r) => r.id === scr)[0] || { icon: '' }).icon || 'ph-squares-four',
    isAccounts: scr === 'accounts',
    isNotifications: scr === 'notifications',
    isSettings: scr === 'settings',
    accounts: acctFiltered,
    acctLedger,
    // §15: the export IS the table — same rows, same order, plain-text columns. Provenance rides the filename:
    // a simulated dataset exports as one, so the file can never claim an authority the screen did not (DEC-107).
    acctExportLedger: () => {
      if (acctLedger.length === 0) {
        c.flash('The ledger is empty — nothing to export.', 'mut');
        return;
      }
      const rows = acctLedger.map((l) =>
        [l.date, l.ev, l.firm, l.detail, l.amt].map((v) => csvEscape(String(v ?? ''))),
      );
      const csv = ['Date,Event,Account,Detail,Amount', ...rows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const tag = c.data.meta.mode === 'simulated' ? '-simulated' : '';
      a.download = 'pointfive-ledger' + tag + '-' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(a.href);
      c.flash(
        'Exported ' +
          rows.length +
          ' ledger row' +
          (rows.length === 1 ? '' : 's') +
          (tag ? ' (simulated data — the filename says so).' : '.'),
        'pos',
      );
    },
    acctTabs,
    notifTabs: (
      [
        ['all', 'All'],
        ['unread', 'Unread'],
        ['overtrading', 'Overtrading'],
        ['loss_limit', 'Loss Limit'],
        ['rule_violation', 'Rule Violations'],
        ['trade_suggestion', 'Suggestions'],
      ] as const
    ).map(([id, label]) => ({
      id,
      label,
      go: () => c.set({ notifTab: id }),
      bg: S.notifTab === id ? 'var(--rail)' : 'transparent',
      fg: S.notifTab === id ? 'var(--rail-ink)' : 'var(--muted)',
    })),
    // §11: mark-all-read is the SERVER's act — the badge and rows re-read from the feed, not from wishful
    // local state, so every surface reading the feed agrees.
    notifMarkAllRead: () => {
      void api.manage.notifications
        .markRead({ all: true })
        .then((r) => {
          refreshDemoData();
          c.flash(
            r.unreadCount === 0
              ? 'All notifications marked read.'
              : `Marked read — ${r.unreadCount} still unread (arrived meanwhile).`,
            'mut',
          );
        })
        .catch((error: unknown) =>
          c.flash(error instanceof Error ? error.message : 'Could not mark notifications read.', 'neg'),
        );
    },
    // §1/§10: Load System Defaults is a SERVER act — every rule whose state differs from the shipped default
    // gets a real riskRules.update, and re-disabling one is a weakening the refractory queue delays (the
    // toast says which landed and which queued rather than pretending).
    rulesLoadDefaults: () => {
      if (!c.S.risk?.loaded) {
        c.flash('Risk rules are still loading from the server — a moment.', 'mut');
        return;
      }
      const targets = Object.entries(initialState.ruleOn) as [string, boolean][];
      void (async () => {
        let applied = 0,
          queued = 0;
        for (const [key, want] of targets) {
          const current = c.S.risk?.rules.find((r) => r.key === key);
          if (!current || current.enabled === want) continue;
          try {
            const saved = await api.prepare.riskRules.update({
              idempotencyKey: `rule-default-${key}-${Date.now()}`,
              key,
              enabled: want,
            });
            const after = saved.state.find((r) => r.key === key);
            c.set((st) =>
              st.risk
                ? { risk: { ...st.risk, rules: saved.state.map((r) => ({ ...r, params: { ...r.params } })) } }
                : {},
            );
            if (after?.effectiveAt) queued += 1;
            else applied += 1;
          } catch {
            /* the per-rule toast is skipped here; the summary below reports totals */
          }
        }
        c.flash(
          queued > 0
            ? `Defaults: ${applied} applied now, ${queued} queued — disabling a rule is a weakening and waits out its delay (ADR-014).`
            : applied > 0
              ? `System defaults restored — ${applied} rule${applied === 1 ? '' : 's'} changed.`
              : 'Every rule already sits at its default.',
          'mut',
        );
      })();
    },
    notifList: D.notifList.map((n) =>
      Object.assign({}, n, {
        dotBd: n.unread
          ? '1px solid color-mix(in srgb, var(--slate-fill) 35%, transparent)'
          : '1px solid transparent',
        rowBg: n.unread ? 'var(--fill)' : 'transparent',
      }),
    ),
    setNav: D.setNav.map((i) =>
      Object.assign({}, i, {
        go: () => c.set({ setSection: i.id }),
        on: S.setSection === i.id,
        bg: S.setSection === i.id ? 'var(--l6)' : 'transparent',
        fg: S.setSection === i.id ? 'var(--text)' : i.danger ? 'var(--neg)' : 'var(--muted)',
      }),
    ),
    // §16: the profile section reads the SERVER's profile and saves through me.profile.update. Email is the
    // sign-in identity and deliberately not editable here; the input says so on its title.
    profileState: S.profile,
    profileLoad: () => {
      if (S.profile?.loaded || S.profile?.busy) return;
      c.set({ profile: { name: '', handle: '', email: '', loaded: false, busy: true, error: null } });
      void api.me.profile
        .get()
        .then((p) =>
          c.set((st) => ({
            profile: {
              name: p.name,
              handle: p.handle,
              email: p.email,
              loaded: true,
              busy: false,
              error: null,
            },
            profileOriginal: { name: p.name, handle: p.handle },
            // The stored photo, when one exists — the header initials are the fallback, not the truth.
            avatarSrc: p.avatarUrl ?? st.avatarSrc,
          })),
        )
        .catch((error: unknown) =>
          c.set({
            profile: {
              name: '',
              handle: '',
              email: '',
              loaded: false,
              busy: false,
              error: error instanceof Error ? error.message : 'The profile could not be loaded.',
            },
          }),
        );
    },
    // §5: Change Photo is a real store — the picked file rides me.profile.setAvatar as a small data URL and
    // the header avatar re-renders from the response's served link.
    profileSetAvatar: (dataUrl: string) => {
      void api.me.profile
        .setAvatar({ idempotencyKey: `avatar-${Date.now()}`, image: dataUrl })
        .then((p) => {
          c.set({ avatarSrc: p.avatarUrl ?? '' });
          c.flash('Profile photo updated.', 'pos');
        })
        .catch((error: unknown) =>
          c.flash(error instanceof Error ? error.message : 'The photo could not be saved.', 'neg'),
        );
    },
    profileName: S.profile?.name ?? '',
    profileHandle: S.profile?.handle ?? '',
    profileEmail: S.profile?.email ?? '',
    profileSetName: (v: string) => c.set((st) => (st.profile ? { profile: { ...st.profile, name: v } } : {})),
    profileSetHandle: (v: string) =>
      c.set((st) => (st.profile ? { profile: { ...st.profile, handle: v.toLowerCase() } } : {})),
    profileSave: () => {
      const p = S.profile;
      if (!p || p.busy) return;
      const name = p.name.trim();
      const handle = p.handle.trim();
      if (!name) return c.flash('The display name cannot be empty.', 'neg');
      // Partial-update semantics, deliberately: only what the trader EDITED goes on the wire. A legacy handle
      // the schema would no longer accept can coexist with a name change — validated only when touched.
      const original = S.profileOriginal;
      const patchBody: { name?: string; handle?: string } = {};
      if (name !== original?.name) patchBody.name = name;
      if (handle !== original?.handle) {
        if (!/^[a-z0-9_]{3,24}$/.test(handle))
          return c.flash('Handles are 3–24 lowercase letters, digits or underscores.', 'neg');
        patchBody.handle = handle;
      }
      if (Object.keys(patchBody).length === 0) return c.flash('Nothing changed.', 'mut');
      c.set({ profile: { ...p, busy: true } });
      void api.me.profile
        .update(patchBody)
        .then((saved) => {
          c.set({
            profile: {
              name: saved.name,
              handle: saved.handle,
              email: saved.email,
              loaded: true,
              busy: false,
              error: null,
            },
            profileOriginal: { name: saved.name, handle: saved.handle },
          });
          c.flash('Profile saved.', 'pos');
        })
        .catch((error: unknown) => {
          c.set({ profile: { ...p, busy: false } });
          c.flash(error instanceof Error ? error.message : 'The profile could not be saved.', 'neg');
        });
    },
    // §4: the notification preferences are SERVER state (me.preferences.*) — a toggle writes through the
    // procedure and the switches re-render from the response's values, so a preference set here is the
    // preference the digest/notification pipeline actually consults, on this and every other device.
    setPrefs: D.setPrefs.map(([k, label, desc]) => {
      const on = !!S.notifPrefs[k],
        t = c.tog(on, 'var(--slate-fill)');
      return Object.assign({ k, label, desc, on }, t, {
        toggle: () =>
          void api.me.preferences
            .update({ values: { [k]: !on } })
            .then((out) => c.set({ notifPrefs: { ...out.values } }))
            .catch((error: unknown) =>
              c.flash(error instanceof Error ? error.message : label + ' could not be saved.', 'neg'),
            ),
      });
    }),
    prefsHydrate: () => {
      if (S.prefsLoaded) return;
      void api.me.preferences
        .get()
        .then((out) => c.set({ notifPrefs: { ...out.values }, prefsLoaded: true }))
        .catch(() => c.set({ prefsLoaded: true }));
    },
    setSectionProfile: S.setSection === 'profile',
    setSectionNotif: S.setSection === 'notifications',
    setSectionOther: !['profile', 'notifications'].includes(S.setSection),
    setSectionLabel: setSectionLabels[S.setSection],
    // aiTabs: returned by the reference, never bound by the template — omitted
    isDashboard: scr === 'dashboard',
    isWidgets: scr === 'widgets',
    isBriefing: scr === 'briefing',
    isPrepare: scr === 'prepare',
    isRiskRules: scr === 'riskrules',
    isRiskProfile: scr === 'riskprofile',
    isStrategies: scr === 'strategies',
    isTradeHub: scr === 'tradehub',
    isSignals: scr === 'signals',
    isTrades: scr === 'trades',
    isCopyTrading: scr === 'copytrading',
    sessions: D.sessions,
    ticker: D.ticker,
    rules,
    activeRules,
    engineStatus: activeRules > 0 ? 'Running' : 'Idle',
    engineStatusFg: activeRules > 0 ? 'var(--pos)' : 'var(--muted2)',
    rcSections,
    rpTabs,
    rpTabControls: S.rpTab === 'controls',
    rpTabMaturity: S.rpTab === 'maturity',
    rpTabAudit: S.rpTab === 'audit',
    moods,
    energy: S.energy,
    energyLabel: 'Energy Level: ' + S.energy + '/10',
    energyFill: S.energy * 10 + '%',
    setEnergy: (e: InputEvent) => c.set({ energy: +val(e) }),
    focusChips,
    stratChips,
    preNoteChips,
    armSession,
    armPending: S.armPending,
    prepRules,
    prepLimits,
    ruleCount: ruleActive + ' of ' + prepRules.length + ' active',
    limitCount: limitActive + ' of ' + prepLimits.length + ' active',
    engineReadyFg: ruleActive > 0 ? 'var(--pos)' : 'var(--amb-2)',
    engineReadyText:
      ruleActive > 0
        ? 'Trade engine will run with ' +
          (ruleActive === 1 ? '1 position rule' : ruleActive + ' position rules')
        : 'No position rules active — the trade engine will stay idle this session',
    // §16: the daily-brief fields are the trader's stated intentions — journal-layer state (preNote's law).
    // The ENFORCED caps stay in the Risk Controls card and ride prepare.session.arm; the Max-trades mirror
    // below shows the enforced number and points at where it changes.
    briefGoal: S.briefGoal,
    briefSetGoal: (v: string) => c.set({ briefGoal: v }),
    briefMaxLoss: S.briefMaxLoss,
    briefSetMaxLoss: (v: string) => c.set({ briefMaxLoss: v }),
    briefTarget: S.briefTarget,
    briefSetTarget: (v: string) => c.set({ briefTarget: v }),
    briefLots: S.briefLots,
    briefSetLots: (v: string) => c.set({ briefLots: v }),
    briefMaxTradesMirror: String(capValue('dtl', 5)),
    briefHistoryGo: () => {
      c.set({ screen: 'eod', mode: SCREEN_MODE['eod'] ?? S.mode });
      c.flash('Past days live in the End-of-Day reports.', 'mut');
    },
    preNote: S.preNote,
    setPreNote: (e: InputEvent) => c.set({ preNote: val(e) }),
    preNoteCount: S.preNote.trim() ? S.preNote.trim().length + ' characters' : 'Not written yet',
    trades,
    tradeRanges,
    wlChips,
    news,
    calendar,
    // tfs: the desk's own timeframe chips went with the hand-drawn chart — the TradingView widget carries its own
    // interval control now. `S.tf` survives because `tvInterval` still reads it. Dead key, omitted.
    ticketTabs,
    posTabs,
    ch: c.chart(),
    posOpen: S.posOpen,
    posToggle: () => c.set((s) => ({ posOpen: !s.posOpen })),
    posToggleIcon: S.posOpen ? 'ph-caret-down' : 'ph-caret-up',
    posToggleLabel: S.posOpen ? 'Collapse positions section' : 'Expand positions section',
    posRows,
    posEmpty: PD.empty,
    posHasRows: posShown > 0,
    posNoRows: posShown === 0,
    posBeShow: S.posTab === 'positions' && S.openPos.length > 0,
    posBeCount: beEligible.length ? 'SL → BE · ' + beEligible.length : 'SL → BE',
    posBeOp: beEligible.length ? '1' : '0.45',
    posBeTitle: beEligible.length
      ? 'Move the stop to entry on ' +
        (beEligible.length === 1
          ? 'the 1 position in profit'
          : 'all ' + beEligible.length + ' positions in profit')
      : beAtAlready.length
        ? beAtAlready.length === 1
          ? 'The 1 position in profit already has its stop at break-even'
          : 'All ' + beAtAlready.length + ' positions in profit already have their stops at break-even'
        : 'No open position is in profit — break-even would sit behind price',
    posBeAll: () =>
      beEligible.length
        ? toBe(
            beEligible.map((o) => o.i),
            (beEligible.length === 1 ? '1 stop' : beEligible.length + ' stops') + ' moved to break-even',
          )
        : c.flash(
            beAtAlready.length
              ? 'Every position in profit is already at break-even'
              : 'No open position is in profit yet',
            'mut',
          ),
    posSectionLabel: PD.label,
    posSummary: PD.total === 0 ? 'None' : PD.total === 1 ? '1 record' : PD.total + ' records',
    // The pager counts what the pipeline actually shows — account scope and symbol filter included — so
    // "Showing 1–3 of 3" while filtered is the truth of the view, not a restatement of the dataset.
    posPager:
      posFiltered.length === 0
        ? 'Nothing to show'
        : 'Showing ' +
          (posPage * POS_PAGE_SIZE + 1) +
          '–' +
          (posPage * POS_PAGE_SIZE + posShown) +
          ' of ' +
          posFiltered.length,
    posFilterSym: S.posFilterSym,
    posFilterSet: (v: string) => c.set({ posFilterSym: v, posPage: 0 }),
    posPagePrev: () =>
      posPage === 0 ? c.flash('Already on the first page.', 'mut') : c.set({ posPage: posPage - 1 }),
    posPageNext: () =>
      posPage >= posPageMax ? c.flash('No further pages.', 'mut') : c.set({ posPage: posPage + 1 }),
    posExportCsv,
    thAcct: S.thAcct,
    thAcctOpen: S.thAcctOpen,
    thAcctToggle: () => c.set((st) => ({ thAcctOpen: !st.thAcctOpen })),
    thAcctClose: () => c.set({ thAcctOpen: false }),
    thAcctLabel: thSelected ? thSelected.firm : 'Session default',
    thAcctChoices,
    // The balance strip follows the account dimension. With no explicit pick the session's account is the
    // SERVER's knowledge, not ours — balance and equity say so rather than restating a design literal that
    // belonged to no verified account. Floating P&L is genuinely known client-side either way.
    thBal:
      thSelected && !thSelected.nodata
        ? '$' + ((thSelected.equity ?? 0) - (thSelected.pnl ?? 0)).toLocaleString('en-US') + '.00'
        : '—',
    thEquity: thSelected && !thSelected.nodata ? thSelected.equityStr + '.00' : '—',
    thPnl:
      (thFloat < 0 ? '−$' : '$') +
      Math.abs(thFloat).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    sideOpen: S.sideOpen,
    sideClosed: !S.sideOpen,
    sideWidth: S.sideOpen ? '304px' : '54px',
    sideToggle: () => c.set((s) => ({ sideOpen: !s.sideOpen })),
    sideToggleIcon: S.sideOpen ? 'ph-caret-double-right' : 'ph-caret-double-left',
    sideToggleLabel: S.sideOpen ? 'Collapse side panel' : 'Expand side panel',
    // chartTools: the hand-drawn chart's drawing toolbar — the widget brings its own. Dead key, omitted.
    rcActiveCount: String(rcActive),
    rcTotalCount: String(rcTotal),
  } satisfies Partial<ViewModel>;
}
export type CoreModel = ReturnType<typeof buildCore>;
